// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConvexMutations
 * @package @geenius/analytics/convex
 * @description Mutation collection for inserting analytics events, page views,
 * identity traits, and derived analytics artifacts into the packaged tables.
 */

import { v } from "convex/values";
import { mutation } from "./_generated/server.js";
import {
  cohortBucketSchema,
  funnelStepResultSchema,
  funnelStepSchema,
  jsonRecordSchema,
  sessionReplayFrameSchema,
} from "./schema.js";

/** Mutation that inserts a normalized analytics event into the packaged events table. */
export const trackEvent = mutation({
  args: {
    id: v.string(),
    sessionId: v.string(),
    name: v.string(),
    type: v.string(),
    properties: v.optional(jsonRecordSchema),
    url: v.string(),
    referrer: v.string(),
    userId: v.optional(v.string()),
    traits: v.optional(jsonRecordSchema),
    device: v.object({
      browser: v.string(),
      os: v.string(),
      device: v.union(
        v.literal("desktop"),
        v.literal("tablet"),
        v.literal("mobile"),
      ),
      screenWidth: v.number(),
      screenHeight: v.number(),
      language: v.string(),
    }),
    timestamp: v.number(),
  },
  returns: v.id("analytics_events"),
  handler: async (ctx, args) => await ctx.db.insert("analytics_events", args),
});

/** Mutation that inserts a normalized page-view record into the packaged pageviews table. */
export const trackPageView = mutation({
  args: {
    url: v.string(),
    title: v.string(),
    referrer: v.string(),
    properties: v.optional(jsonRecordSchema),
    duration: v.optional(v.number()),
    sessionId: v.string(),
    userId: v.optional(v.string()),
    timestamp: v.number(),
  },
  returns: v.id("analytics_pageviews"),
  handler: async (ctx, args) =>
    await ctx.db.insert("analytics_pageviews", args),
});

/** Mutation that upserts analytics user traits keyed by external user identifier. */
export const identifyUser = mutation({
  args: { userId: v.string(), traits: jsonRecordSchema },
  returns: v.null(),
  handler: async (ctx, { userId, traits }) => {
    const existing = await ctx.db
      .query("analytics_users")
      .withIndex("by_userId", (q) => q.eq("userId", userId))
      .first();
    if (existing) {
      await ctx.db.patch(existing._id, { traits, lastSeen: Date.now() });
    } else {
      await ctx.db.insert("analytics_users", {
        userId,
        traits,
        firstSeen: Date.now(),
        lastSeen: Date.now(),
      });
    }
    return null;
  },
});

/** Mutation that merges profile traits for an existing or newly identified user. */
export const setUserProperties = mutation({
  args: { userId: v.string(), traits: jsonRecordSchema },
  returns: v.null(),
  handler: async (ctx, { userId, traits }) => {
    const now = Date.now();
    const existing = await ctx.db
      .query("analytics_users")
      .withIndex("by_userId", (q) => q.eq("userId", userId))
      .first();
    if (existing) {
      await ctx.db.patch(existing._id, {
        traits: { ...existing.traits, ...traits },
        lastSeen: now,
      });
    } else {
      await ctx.db.insert("analytics_users", {
        userId,
        traits,
        firstSeen: now,
        lastSeen: now,
      });
    }
    return null;
  },
});

/** Mutation that persists evaluated funnel-analysis results for later querying. */
export const storeFunnelResult = mutation({
  args: {
    name: v.string(),
    steps: v.array(funnelStepSchema),
    results: v.array(funnelStepResultSchema),
    totalEntries: v.number(),
    totalConversions: v.number(),
    overallConversionRate: v.number(),
  },
  returns: v.id("analytics_funnels"),
  handler: async (ctx, args) =>
    await ctx.db.insert("analytics_funnels", {
      ...args,
      analyzedAt: Date.now(),
    }),
});

/** Mutation that persists generated cohort analyses for later querying. */
export const storeCohortAnalysis = mutation({
  args: {
    name: v.string(),
    cohortType: v.union(v.literal("user"), v.literal("session")),
    intervals: v.array(v.number()),
    cohorts: v.array(cohortBucketSchema),
  },
  returns: v.id("analytics_cohorts"),
  handler: async (ctx, args) =>
    await ctx.db.insert("analytics_cohorts", {
      ...args,
      generatedAt: Date.now(),
    }),
});

/** Mutation that stores a normalized session-replay snapshot for a session identifier. */
export const storeSessionReplay = mutation({
  args: {
    sessionId: v.string(),
    startedAt: v.number(),
    endedAt: v.number(),
    duration: v.number(),
    frames: v.array(sessionReplayFrameSchema),
    activePages: v.array(v.string()),
    userId: v.optional(v.string()),
  },
  returns: v.id("analytics_session_replays"),
  handler: async (ctx, args) =>
    await ctx.db.insert("analytics_session_replays", args),
});

/** Mutation that deletes all analytics data for a given user (right-to-forget compliance). */
export const deleteUserData = mutation({
  args: { userId: v.string() },
  returns: v.null(),
  handler: async (ctx, { userId }) => {
    const events = await ctx.db
      .query("analytics_events")
      .filter((q) => q.eq(q.field("userId"), userId))
      .collect();
    for (const event of events) {
      await ctx.db.delete(event._id);
    }

    const pageViews = await ctx.db
      .query("analytics_pageviews")
      .filter((q) => q.eq(q.field("userId"), userId))
      .collect();
    for (const pageView of pageViews) {
      await ctx.db.delete(pageView._id);
    }

    const user = await ctx.db
      .query("analytics_users")
      .withIndex("by_userId", (q) => q.eq("userId", userId))
      .first();
    if (user) {
      await ctx.db.delete(user._id);
    }

    return null;
  },
});

/** Mutation that deletes analytics rows older than the supplied retention cutoff. */
export const purgeExpiredData = mutation({
  args: { cutoffTimestamp: v.number() },
  returns: v.null(),
  handler: async (ctx, { cutoffTimestamp }) => {
    const events = await ctx.db
      .query("analytics_events")
      .withIndex("by_timestamp", (q) => q.lt("timestamp", cutoffTimestamp))
      .collect();
    for (const event of events) {
      await ctx.db.delete(event._id);
    }

    const pageViews = await ctx.db
      .query("analytics_pageviews")
      .withIndex("by_timestamp", (q) => q.lt("timestamp", cutoffTimestamp))
      .collect();
    for (const pageView of pageViews) {
      await ctx.db.delete(pageView._id);
    }

    const users = await ctx.db
      .query("analytics_users")
      .filter((q) => q.lt(q.field("lastSeen"), cutoffTimestamp))
      .collect();
    for (const user of users) {
      await ctx.db.delete(user._id);
    }

    return null;
  },
});
